/**
 * DryRunViewer - Animated step-by-step algorithm execution
 */
import React, { useState, useEffect, useRef } from 'react';
import './DryRunViewer.css';

function DryRunViewer({ dryRun, algorithmName }) {
  const [currentStep, setCurrentStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1500);
  const intervalRef = useRef(null);

  const steps = dryRun?.steps || [];
  const result = dryRun?.result;

  // Auto-play
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= steps.length - 1) {
            setPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, speed);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, steps.length]);

  const handlePlay = () => {
    if (currentStep >= steps.length - 1) {
      setCurrentStep(0);
    }
    setPlaying(true);
  };

  const handleReset = () => {
    setPlaying(false);
    setCurrentStep(-1);
    clearInterval(intervalRef.current);
  };

  const handleStepForward = () => {
    setPlaying(false);
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleStepBack = () => {
    setPlaying(false);
    setCurrentStep(prev => Math.max(prev - 1, -1));
  };

  const visibleSteps = currentStep >= 0 ? steps.slice(0, currentStep + 1) : [];

  return (
    <div className="dryrun">
      {/* Input Display */}
      {dryRun?.input && (
        <div className="dryrun-input">
          <h4>Input</h4>
          <pre>{JSON.stringify(dryRun.input, null, 2)}</pre>
        </div>
      )}

      {/* Controls */}
      <div className="dryrun-controls">
        <button className="drybtn" onClick={handleReset} title="Reset">⏮</button>
        <button className="drybtn" onClick={handleStepBack} disabled={currentStep < 0} title="Step back">⏪</button>
        {playing ? (
          <button className="drybtn drybtn-main" onClick={() => setPlaying(false)} title="Pause">⏸ Pause</button>
        ) : (
          <button className="drybtn drybtn-main" onClick={handlePlay} title="Play">▶ Play</button>
        )}
        <button className="drybtn" onClick={handleStepForward} disabled={currentStep >= steps.length - 1} title="Step forward">⏩</button>

        <div className="speed-control">
          <label>Speed</label>
          <select value={speed} onChange={e => setSpeed(Number(e.target.value))}>
            <option value={2500}>0.5×</option>
            <option value={1500}>1×</option>
            <option value={800}>2×</option>
            <option value={400}>4×</option>
          </select>
        </div>

        <span className="step-counter">
          Step {Math.max(currentStep, 0)} / {steps.length}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="dryrun-progress">
        <div
          className="dryrun-progress-fill"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      {/* Steps */}
      <div className="dryrun-steps">
        {currentStep === -1 && (
          <div className="dryrun-prompt">
            <span>▶</span>
            <p>Press <strong>Play</strong> or <strong>Step Forward</strong> to begin the dry run</p>
          </div>
        )}

        {visibleSteps.map((step, idx) => (
          <div
            key={idx}
            className={`dryrun-step ${idx === currentStep ? 'active' : ''}`}
          >
            <div className="step-num">{step.step || idx + 1}</div>
            <div className="step-content">
              <p className="step-action">{step.action}</p>
              {step.running !== undefined && (
                <div className="step-state">
                  <span className="state-label">Running:</span>
                  <span className={`state-val ${step.running ? 'running' : 'idle'}`}>
                    {step.running || 'idle'}
                  </span>
                </div>
              )}
              {step.head !== undefined && (
                <div className="step-state">
                  <span className="state-label">Head position:</span>
                  <span className="state-val">{step.head}</span>
                  {step.seek !== undefined && (
                    <span className="state-seek">seek: {step.seek}</span>
                  )}
                </div>
              )}
              {step.frames && (
                <div className="step-frames">
                  <span className="state-label">Frames:</span>
                  <div className="frame-boxes">
                    {step.frames.map((f, i) => (
                      <div key={i} className={`frame-box ${f === step.page ? 'new' : ''}`}>{f}</div>
                    ))}
                  </div>
                  <span className={`fault-badge ${step.fault ? 'fault' : 'hit'}`}>
                    {step.fault ? 'PAGE FAULT' : 'HIT'}
                  </span>
                </div>
              )}
              {step.safe && (
                <div className="step-state">
                  <span className="state-label">Safe sequence so far:</span>
                  <span className="state-val text-green">{step.safe.join(' → ')}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Result */}
      {currentStep >= steps.length - 1 && result && (
        <div className="dryrun-result">
          <h4>✅ Final Result</h4>
          <pre>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

export default DryRunViewer;
